import axios, { AxiosResponse } from 'axios';
import { createApiClient, ApiClientConfig } from './apiClientWithRefresh';
import { getApiTimeout } from '../config/apiConfig';
import { handleApiError } from '../config/apiConfig';
import { appConfig } from '../config/appConfig';

// User access types
export interface AppAccess {
  access: "Yes" | "No";
  url: string | null;
}

export interface AccessResponse {
  user_id: string;
  access: {
    [appName: string]: AppAccess;
  };
}

export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  error?: string;
}

// Token and refresh callback storage - will be set when auth context is available
let currentAccessToken: (() => string | undefined) | null = null;
let currentRefreshCallback: (() => Promise<boolean>) | null = null;

// Request deduplication - prevent concurrent duplicate calls to /access
let pendingAccessRequest: Promise<ApiResponse<AccessResponse>> | null = null;

// Create API client with token refresh functionality
const createConfiguredApiClient = () => {
  
  const baseURL = appConfig.API_BASE_URL;
  
  if (!baseURL) {
    console.error('CRITICAL: API_BASE_URL not configured!');
    throw new Error('API base URL is not configured. Please check appConfig.ts configuration.');
  }
  
  if (!currentAccessToken || !currentRefreshCallback) {
    // Fallback to basic axios instance if auth is not available
    return axios.create({
      baseURL: baseURL,
      timeout: getApiTimeout('default'),
      headers: {
        'Content-Type': 'application/json',
      },
    });
  }

  const config: ApiClientConfig = {
    baseURL: baseURL,
    timeout: getApiTimeout('default'),
    getAccessToken: currentAccessToken,
    refreshTokenCallback: currentRefreshCallback,
    onTokenRefreshFailed: () => {
      // Could trigger logout or redirect to login here
    }
  };

  return createApiClient(config);
};

// Initialize API client
let apiClient = createConfiguredApiClient();

// Function to update the API client with auth context
export const configureApiClient = (
  getAccessToken: () => string | undefined,
  refreshTokenCallback: () => Promise<boolean>
) => {
  
  currentAccessToken = getAccessToken;
  currentRefreshCallback = refreshTokenCallback;
  apiClient = createConfiguredApiClient();
  
  // Add response interceptor to the newly configured client
  apiClient.interceptors.response.use(
    (response: AxiosResponse) => {
      return response;
    },
    (error) => {
    const operation = error.config?.url?.includes('/access') ? 'ACCESS' : 'MAIN-API';
    handleApiError(error, operation);
      return Promise.reject(error);
    }
  );
};

/**
 * Send Authorization header with id_token to the access API endpoint
 * Implements request deduplication to prevent multiple simultaneous calls
 * @returns Promise with access response
 */
export const checkUserAccess = async (): Promise<ApiResponse<AccessResponse>> => {
  // If a request is already in progress, return the existing promise
  // This prevents duplicate concurrent calls to /access endpoint
  if (pendingAccessRequest) {
    return pendingAccessRequest;
  }

  // Create new request promise
  pendingAccessRequest = (async () => {
    try {
      const response = await apiClient.get<AccessResponse>(`/access`);
      return {
        success: true,
        data: response.data,
        message: 'User access checked successfully'
      };
    } catch (error: any) {
      handleApiError(error, 'ACCESS-API');
      return {
        success: false,
        error: error.message || 'Failed to check user access'
      };
    } finally {
      // Clear pending request after completion (success or failure)
      pendingAccessRequest = null;
    }
  })();

  return pendingAccessRequest;
};

export default apiClient;
